// PageDescription: 실시간 의료 상담 리스트
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import QnaRealtimeTemplate from '@/components/templates/mobile/communication/Qna/QnaRealtimeTemplate';
import { useGetNewestQuestionCardCursorPageQuery, useGetQnaCategoryGroupQuery } from '@/hooks/qna/mobile/qna.gql.impl';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

interface Props extends CustomPageProps {
  categoryId: string;
}

const QnaRealtime = ({ client, categoryId }: Props) => {
  return (
    <>
      <NextSeo
        title='실시간 의료상담 - 닥터나우'
        description='24시간 대기하고 있는 닥터나우 의료인들이 실시간으로 답변해 드려요.'
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.QNA.REALTIME.path}`}
        openGraph={{
          title: '실시간 의료상담 - 닥터나우',
          description: '24시간 대기하고 있는 닥터나우 의료인들이 실시간으로 답변해 드려요.',
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.QNA.REALTIME.path}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_QNA_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <QnaRealtimeTemplate client={client} categoryId={categoryId} />
    </>
  );
};

QnaRealtime.Layout = DefaultLayout;

export default QnaRealtime;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const queryClient = new QueryClient();
  const categoryId = (ctx.query.categoryId as string) ?? null;

  const NEWEST_QUESTION_SIZE = 20;

  const variables = {
    size: NEWEST_QUESTION_SIZE,
    answered: false,
    categoryId,
  };

  await Promise.allSettled([
    queryClient.prefetchInfiniteQuery(
      useGetNewestQuestionCardCursorPageQuery.getKey({
        request: { ...variables },
      }),
      useGetNewestQuestionCardCursorPageQuery.fetcher({
        request: { ...variables },
      })
    ),
    queryClient.prefetchQuery(useGetQnaCategoryGroupQuery.getKey(), useGetQnaCategoryGroupQuery.fetcher()),
  ]);

  return {
    props: {
      categoryId,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
